import { StrictMode } from 'react'
import './i18n'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import QueryProvider from './providers/QueryProvider.tsx'

import settingsService from './services/api/settings.service'

function appendHtmlWithExecutableScripts(target: HTMLElement, html: string) {
  const template = document.createElement('template')
  template.innerHTML = html

  const nodes = Array.from(template.content.childNodes)
  for (const node of nodes) {
    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === 'script') {
      const oldScript = node as HTMLScriptElement
      const newScript = document.createElement('script')

      for (const attr of Array.from(oldScript.attributes)) {
        newScript.setAttribute(attr.name, attr.value)
      }

      if (oldScript.textContent) {
        newScript.text = oldScript.textContent
      }

      target.appendChild(newScript)
      continue
    }

    target.appendChild(node)
  }
}

async function injectTrackingScriptsOnce() {
  try {
    const settings = await settingsService.getSettings()
    const headCode = (settings.tracking_head_code || '').trim()
    const bodyCode = (settings.tracking_body_code || '').trim()

    if (!headCode && !bodyCode) return

    if (headCode) {
      const headMarkerId = 'sbb-tracking-head-marker'
      if (!document.getElementById(headMarkerId)) {
        const marker = document.createElement('meta')
        marker.id = headMarkerId
        marker.setAttribute('name', 'sbb-tracking-head-injected')
        marker.setAttribute('content', '1')
        document.head.appendChild(marker)

        appendHtmlWithExecutableScripts(document.head, headCode)
      }
    }

    if (bodyCode) {
      const bodyMarkerId = 'sbb-tracking-body-root'
      if (!document.getElementById(bodyMarkerId)) {
        const root = document.createElement('div')
        root.id = bodyMarkerId
        root.style.display = 'none'
        document.body.insertAdjacentElement('afterbegin', root)

        appendHtmlWithExecutableScripts(root, bodyCode)
      }
    }
  } catch {
    // Ignore tracking injection failures
  }
}

// Set initial direction and language based on stored language
const storedLang = localStorage.getItem('language') || 'ar';
document.documentElement.dir = storedLang === 'ar' ? 'rtl' : 'ltr';
document.documentElement.lang = storedLang;

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryProvider>
      <App />
    </QueryProvider>
  </StrictMode>,
)

// Fire and forget
void injectTrackingScriptsOnce()
